import { type CSSProperties } from 'react'
import { LineWeight } from '@opallabs/gem'
import { type PlacedBar, type PlacedSpan } from '../laneLayout'
import { barColors } from '../resolveItemColor'
import { CalendarSizes } from '../sizes'
import * as styles from './styles'

/** Which ends of a run of days are real, and how many columns it covers. */
type RunEdges = Readonly<{ isEnd: boolean; isStart: boolean; span: number }>

/**
 * How wide a run of days is, and how far into its first cell it begins.
 *
 * The cells are the same width and have no gap between them, so `span × 100%`
 * would land on the right edge of the last day covered — except that a cell
 * paints a rule down its trailing edge, and an absolutely positioned child
 * measures `100%` against the box inside that rule. The run adds one line back
 * for every column after the first. Without it a bar that reaches over five
 * days stops four pixels short of one that reaches over one, and two runs that
 * end on the same day no longer line up.
 *
 * An end the run carries past this week row runs to the cell wall. A real end
 * is held in by `itemInsetInline`, the same distance the chip column holds its
 * chips in by.
 */
const runGeometry = ({ isEnd, isStart, span }: RunEdges): CSSProperties => {
  const startInset = isStart ? CalendarSizes.itemInsetInline : 0
  const endInset = isEnd ? CalendarSizes.itemInsetInline : 0
  const offset = (span - 1) * LineWeight.Small - startInset - endInset

  return {
    insetInlineStart: `${startInset}px`,
    inlineSize: `calc(${span} * 100% ${offset < 0 ? '-' : '+'} ${Math.abs(
      offset
    )}px)`,
  }
}

/**
 * Places a bar inside the day cell it starts in.
 *
 * A bar lives in its starting cell so it stays a child of that day for assistive
 * technology, and reaches across the days after it by being as wide as several
 * columns.
 */
export const barPositionStyle = (bar: PlacedBar): CSSProperties =>
  ({
    [styles.cssVariables.barLane]: bar.lane,
    ...runGeometry(bar),
  }) as CSSProperties

/**
 * Places a content span inside the day cell it starts in.
 *
 * Like a bar, a span lives in its starting cell so that it stays a child of
 * that day for assistive technology. It is offset down the chip column by the
 * row it was given, which every day it covers keeps empty for it.
 */
export const spanPositionStyle = (span: PlacedSpan): CSSProperties =>
  ({
    [styles.cssVariables.spanRow]: span.row,
    ...runGeometry(span),
  }) as CSSProperties

/** Publishes an item's derived fill and border colors to the stylesheet. */
export const itemColorStyle = (
  color: string | undefined
): CSSProperties | undefined => {
  const derived = color ? barColors(color) : null
  if (!derived) return undefined

  return {
    [styles.cssVariables.itemBorder]: derived.border,
    [styles.cssVariables.itemFill]: derived.fill,
    [styles.cssVariables.itemHoverFill]: derived.hoverFill,
  } as CSSProperties
}
